import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { deliveriesAPI } from '../../api/deliveries'
import type { Delivery } from '../../types'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString('fr-TN', { day: '2-digit', month: 'short' }) : '—'

const STATUT_CONFIG: Record<string, { bg: string; color: string; label: string }> = {
  failed:         { bg: '#FEF2F2', color: '#DC2626', label: 'Échec' },
  return_pending: { bg: '#FFFBEB', color: '#92400E', label: 'Retour en cours' },
  returned:       { bg: '#F5F3FF', color: '#6D28D9', label: 'Retourné' },
  rescheduled:    { bg: '#EFF6FF', color: '#1D4ED8', label: 'Reprogrammé' },
}

function StatutBadge({ status }: { status: string }) {
  const cfg = STATUT_CONFIG[status] ?? { bg: '#F3F4F6', color: '#6B7280', label: status }
  return (
    <span style={{
      background: cfg.bg, color: cfg.color, fontSize: 11, fontWeight: 700,
      padding: '3px 10px', borderRadius: 999,
    }}>
      {cfg.label}
    </span>
  )
}

// ─── Decision Modal ────────────────────────────────────────────────────────────

interface DecisionModalProps {
  delivery: Delivery
  onClose: () => void
  onSuccess: () => void
}

function DecisionModal({ delivery, onClose, onSuccess }: DecisionModalProps) {
  const fi = (delivery as any).failureInfo as any
  const attempts    = fi?.attempts    ?? 0
  const maxAttempts = fi?.maxAttempts ?? 3
  const canReschedule = attempts < maxAttempts

  const [action,         setAction]         = useState<'reschedule' | 'return' | 'abandon' | ''>('')
  const [rescheduledFor, setRescheduledFor] = useState('')
  const [abandonMotif,   setAbandonMotif]   = useState('')
  const [isLoading,      setIsLoading]      = useState(false)
  const [error,          setError]          = useState('')

  const handleSubmit = async () => {
    if (!action) { setError('Choisissez une action'); return }
    if (action === 'reschedule' && !rescheduledFor) { setError('Date de reprogrammation requise'); return }
    if (action === 'abandon' && !abandonMotif.trim()) { setError('Motif d\'abandon requis'); return }

    setIsLoading(true)
    setError('')
    try {
      if (action === 'reschedule') {
        await deliveriesAPI.reprogrammerColis(delivery._id, rescheduledFor)
        toast.success('Colis reprogrammé')
      } else if (action === 'return') {
        await deliveriesAPI.demanderRetour(delivery._id)
        toast.success('Retour demandé au livreur')
      } else {
        await deliveriesAPI.abandonnerColis(delivery._id, abandonMotif)
        toast.success('Colis classé sans suite')
      }
      onSuccess()
      onClose()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur')
    } finally {
      setIsLoading(false)
    }
  }

  const inp: React.CSSProperties = {
    width: '100%', padding: '9px 12px', borderRadius: 8, fontSize: 13,
    border: '1px solid #E5E7EB', boxSizing: 'border-box', fontFamily: 'inherit',
  }

  const actionCard = (
    id: 'reschedule' | 'return' | 'abandon',
    icon: string,
    title: string,
    desc: string,
    disabled?: boolean
  ) => (
    <button
      onClick={() => !disabled && setAction(id)}
      disabled={disabled}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 12,
        padding: '14px 16px', borderRadius: 12, border: `2px solid`,
        borderColor: action === id ? '#1D9E75' : '#E5E7EB',
        background: action === id ? '#F0FDF4' : disabled ? '#F9FAFB' : '#fff',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1, textAlign: 'left', width: '100%',
        fontFamily: 'inherit',
      }}
    >
      <span style={{ fontSize: 20 }}>{icon}</span>
      <div>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#0D1117', marginBottom: 3 }}>{title}</div>
        <div style={{ fontSize: 12, color: '#6B7280' }}>{desc}</div>
      </div>
    </button>
  )

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: 28,
        width: '100%', maxWidth: 460, maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 8px 40px rgba(0,0,0,.18)',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: '1.05rem', color: '#0D1117' }}>
            Décision — {delivery.trackingCode}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9CA3AF' }}>×</button>
        </div>

        {/* Résumé */}
        <div style={{
          background: '#F9FAFB', borderRadius: 10, padding: '12px 16px', marginBottom: 20,
          fontSize: 13, display: 'flex', flexDirection: 'column', gap: 4,
        }}>
          <span>Motif : <strong>{fi?.reasonLabel ?? '—'}</strong></span>
          <span>Tentatives : <strong style={{ color: attempts >= maxAttempts ? '#EF4444' : '#374151' }}>
            {attempts} / {maxAttempts}
          </strong></span>
          {fi?.note && <span style={{ fontStyle: 'italic', color: '#6B7280' }}>"{fi.note}"</span>}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
          {actionCard('reschedule', '📅', 'Reprogrammer',
            canReschedule
              ? `Nouvelle tentative (${attempts}/${maxAttempts} utilisées)`
              : `Max atteint (${maxAttempts}/${maxAttempts}) — impossible`,
            !canReschedule
          )}
          {actionCard('return', '↩️', 'Retour expéditeur',
            'Le livreur ramène le colis à l\'entrepôt ou chez l\'expéditeur'
          )}
          {actionCard('abandon', '🗑️', 'Classer sans suite',
            'Le colis est définitivement clos (perte, accord expéditeur…)'
          )}
        </div>

        {action === 'reschedule' && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              Date de la prochaine tentative *
            </label>
            <input
              type="date"
              value={rescheduledFor}
              onChange={e => setRescheduledFor(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              style={inp}
            />
          </div>
        )}

        {action === 'abandon' && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
              Motif de classement *
            </label>
            <textarea
              value={abandonMotif}
              onChange={e => setAbandonMotif(e.target.value)}
              placeholder="Colis perdu, accord amiable avec l'expéditeur…"
              rows={3}
              style={{ ...inp, resize: 'vertical' }}
            />
          </div>
        )}

        {error && <p style={{ color: '#EF4444', fontSize: 13, margin: '0 0 12px' }}>{error}</p>}

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: '11px 0', borderRadius: 12, border: '1px solid #E5E7EB',
            background: '#F9FAFB', color: '#374151', fontSize: 14, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'inherit',
          }}>Annuler</button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || !action}
            style={{
              flex: 1, padding: '11px 0', borderRadius: 12, border: 'none',
              background: '#0D1117', color: '#fff', fontSize: 14, fontWeight: 600,
              cursor: isLoading || !action ? 'not-allowed' : 'pointer',
              opacity: !action ? 0.5 : 1, fontFamily: 'inherit',
            }}
          >
            {isLoading ? 'Traitement...' : 'Confirmer'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

const FILTER_TABS = [
  { value: '',               label: 'Tous' },
  { value: 'failed',         label: 'Échecs' },
  { value: 'rescheduled',    label: 'Reprogrammés' },
  { value: 'return_pending', label: 'Retours en cours' },
  { value: 'returned',       label: 'Retournés' },
]

export default function AdminEchecsPage() {
  const navigate   = useNavigate()
  const qc         = useQueryClient()
  const [statut, setStatut] = useState('')
  const [page,   setPage]   = useState(1)
  const [selected, setSelected] = useState<Delivery | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['echecs', statut, page],
    queryFn:  () => deliveriesAPI.getEchecs({ statut: statut || undefined, page }),
  })

  const deliveries: Delivery[] = data?.deliveries ?? []
  const total: number          = data?.pagination?.total ?? 0
  const totalPages: number     = data?.pagination?.totalPages ?? 1

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['echecs'] })
    setSelected(null)
  }

  return (
    <>
      {selected && (
        <DecisionModal
          delivery={selected}
          onClose={() => setSelected(null)}
          onSuccess={invalidate}
        />
      )}

      <div className="page-head">
        <div className="ph-titles">
          <h1>Exceptions</h1>
          <p>Colis en échec, retour ou reprogrammés nécessitant une décision.</p>
        </div>
      </div>

      {/* Filtre tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {FILTER_TABS.map(t => (
          <button
            key={t.value}
            onClick={() => { setStatut(t.value); setPage(1) }}
            style={{
              padding: '7px 16px', borderRadius: 20, border: '1.5px solid',
              borderColor: statut === t.value ? '#0D1117' : '#E5E7EB',
              background: statut === t.value ? '#0D1117' : '#fff',
              color: statut === t.value ? '#fff' : '#374151',
              fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            {t.label}
          </button>
        ))}
        {total > 0 && (
          <span style={{ marginLeft: 'auto', alignSelf: 'center', fontSize: 13, color: '#9CA3AF' }}>
            {total} colis
          </span>
        )}
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #F3F4F6', overflow: 'hidden' }}>
        {isLoading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Chargement…</div>
        ) : deliveries.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>
            Aucun colis dans cette catégorie.
          </div>
        ) : (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #F3F4F6' }}>
                {['COLIS', 'LIVREUR', 'MOTIF', 'TENTATIVES', 'STATUT', 'DATE', 'ACTION'].map(h => (
                  <th key={h} style={{
                    padding: '10px 14px', textAlign: 'left',
                    fontWeight: 700, color: '#9CA3AF', fontSize: 10, letterSpacing: '0.04em',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {deliveries.map(d => {
                const fi        = (d as any).failureInfo as any
                const attempts  = fi?.attempts ?? 0
                const max       = fi?.maxAttempts ?? 3
                const livreur   = (d.livreur as any)
                const isUrgent  = attempts >= max && d.status === 'failed'
                return (
                  <tr
                    key={d._id}
                    style={{ borderBottom: '1px solid #F9FAFB', background: isUrgent ? '#FEF2F2' : undefined }}
                  >
                    <td style={{ padding: '12px 14px' }}>
                      <button
                        onClick={() => navigate(`/admin/livraisons/${d._id}`)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          fontFamily: 'monospace', fontWeight: 700, color: '#0D1117', fontSize: 12, padding: 0,
                        }}
                      >
                        {d.trackingCode}
                      </button>
                    </td>
                    <td style={{ padding: '12px 14px', color: '#374151' }}>
                      {livreur?.name ?? <span style={{ color: '#9CA3AF' }}>Non assigné</span>}
                    </td>
                    <td style={{ padding: '12px 14px', color: '#6B7280', maxWidth: 180 }}>
                      {fi?.reasonLabel ?? '—'}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ fontWeight: 700, color: isUrgent ? '#DC2626' : '#374151' }}>
                        {attempts}/{max}
                      </span>
                      {isUrgent && <span style={{ marginLeft: 4, fontSize: 10, color: '#DC2626', fontWeight: 700 }}>MAX</span>}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <StatutBadge status={d.status} />
                    </td>
                    <td style={{ padding: '12px 14px', color: '#9CA3AF' }}>
                      {fmtDate(d.updatedAt)}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      {['failed', 'rescheduled'].includes(d.status) && (
                        <button
                          onClick={() => setSelected(d)}
                          style={{
                            padding: '6px 12px', borderRadius: 8, border: 'none',
                            background: isUrgent ? '#EF4444' : '#0D1117',
                            color: '#fff', fontSize: 12, fontWeight: 600,
                            cursor: 'pointer', fontFamily: 'inherit',
                          }}
                        >
                          Décider
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
            <button
              key={p}
              onClick={() => setPage(p)}
              style={{
                width: 34, height: 34, borderRadius: 8, border: '1px solid',
                borderColor: page === p ? '#0D1117' : '#E5E7EB',
                background: page === p ? '#0D1117' : '#fff',
                color: page === p ? '#fff' : '#374151',
                fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              {p}
            </button>
          ))}
        </div>
      )}
    </>
  )
}
